import { useState, useMemo, useRef } from 'react'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { useFinance } from '../../context/FinanceContext'
import { useSettings } from '../../context/SettingsContext'
import { getThemeColors } from '../../lib/constants'
import { formatCurrency, formatCurrencyExact } from '../../lib/formatters'
import {
  calculateInterestSavings,
  calculateMortgageExtras,
  generateAmortizationWithPMI,
  LOAN_PRESETS,
} from '../../lib/calculations'
import GlassCard from '../ui/GlassCard'
import Button from '../ui/Button'
import { addChartToPDF } from '../../lib/pdfChartCapture'
import { useSubscription } from '../../context/SubscriptionContext'
import { trackExportReport } from '../../lib/analytics'

export default function LoanCalculator() {
  const { state } = useFinance()
  const { settings } = useSettings()
  const { isPro } = useSubscription()
  const colors = getThemeColors(state.theme)
  const ld = settings.calculatorDefaults.loan

  const [loanType, setLoanType] = useState('mortgage')
  const preset = LOAN_PRESETS[loanType]

  const [principal, setPrincipal] = useState(ld.principal)
  const [rate, setRate] = useState(preset.defaultRate)
  const [termMonths, setTermMonths] = useState(preset.defaultTerm)
  const [downPayment, setDownPayment] = useState(ld.downPayment)
  const [downPaymentMode, setDownPaymentMode] = useState('dollar') // 'dollar' | 'percent'
  const [downPaymentPercent, setDownPaymentPercent] = useState(20)
  const [extraPayment, setExtraPayment] = useState(0)
  const [showAmortization, setShowAmortization] = useState(false)
  const chartRef = useRef(null)

  // Mortgage-specific
  const [propertyTaxRate, setPropertyTaxRate] = useState(1.2)
  const [propertyTaxMode, setPropertyTaxMode] = useState('percent') // 'percent' | 'dollar'
  const [annualPropertyTax, setAnnualPropertyTax] = useState(4200)
  const [pmiRate, setPmiRate] = useState(0.75)
  const [annualInsurance, setAnnualInsurance] = useState(1800)
  const [monthlyHOA, setMonthlyHOA] = useState(0)

  const handlePreset = (type) => {
    const p = LOAN_PRESETS[type]
    setLoanType(type)
    setRate(p.defaultRate)
    setTermMonths(p.defaultTerm)
    if (!p.hasDownPayment) {
      setDownPayment(0)
      setDownPaymentPercent(0)
    }
    if (!p.isMortgage) {
      setPropertyTaxRate(0)
      setPmiRate(0)
      setAnnualInsurance(0)
      setMonthlyHOA(0)
    } else {
      setPropertyTaxRate(1.2)
      setPmiRate(0.75)
      setAnnualInsurance(1800)
      setMonthlyHOA(0)
    }
  }

  // Compute effective down payment
  const effectiveDownPayment = downPaymentMode === 'percent'
    ? Math.round(principal * downPaymentPercent / 100)
    : downPayment
  const effectiveDownPaymentPercent = principal > 0
    ? (effectiveDownPayment / principal) * 100
    : 0

  // Auto-zero PMI when down payment >= 20% for mortgages
  const effectivePmiRate = (preset.isMortgage && effectiveDownPaymentPercent >= 20)
    ? 0
    : pmiRate

  const result = useMemo(() =>
    calculateInterestSavings(principal, rate, termMonths, extraPayment, effectiveDownPayment),
    [principal, rate, termMonths, extraPayment, effectiveDownPayment]
  )

  // Effective property tax — from rate or from dollar amount
  const effectivePropertyTaxRate = propertyTaxMode === 'dollar' && principal > 0
    ? (annualPropertyTax / principal) * 100
    : propertyTaxRate
  const effectiveAnnualPropertyTax = propertyTaxMode === 'percent'
    ? principal * propertyTaxRate / 100
    : annualPropertyTax

  // Mortgage extras
  const loanAmount = principal - effectiveDownPayment
  const extras = useMemo(() => {
    if (!preset.isMortgage) return null
    return calculateMortgageExtras(principal, loanAmount > 0 ? loanAmount : 0, {
      propertyTaxRate: effectivePropertyTaxRate,
      pmiRate: effectivePmiRate,
      annualInsurance,
      monthlyHOA,
    })
  }, [principal, loanAmount, effectivePropertyTaxRate, effectivePmiRate, annualInsurance, monthlyHOA, preset.isMortgage])

  // Amortization with PMI tracking
  const mortgageAmort = useMemo(() => {
    if (!preset.isMortgage || !extras || loanAmount <= 0) return null
    return generateAmortizationWithPMI(loanAmount, rate, termMonths, principal, {
      ...extras,
      pmiRate: effectivePmiRate,
    }, extraPayment)
  }, [loanAmount, rate, termMonths, principal, extras, effectivePmiRate, extraPayment, preset.isMortgage])

  const totalMonthly = preset.isMortgage && extras
    ? result.monthlyPayment + extras.totalMonthlyExtras + extraPayment
    : result.monthlyPayment + extraPayment

  // PMI drop-off month
  const pmiDropMonth = mortgageAmort?.schedule.find(r => r.pmi === 0 && effectivePmiRate > 0)?.month

  const chartData = useMemo(() => {
    const schedule = mortgageAmort?.schedule || result.schedule
    if (!schedule) return []
    return schedule.filter((_, i) => i % Math.max(1, Math.floor(schedule.length / 60)) === 0 || i === schedule.length - 1)
      .map(row => ({
        month: row.month,
        label: `Mo ${row.month}`,
        balance: row.balance,
        principalPaid: row.totalPrincipal,
        interestPaid: row.totalInterest,
      }))
  }, [result, mortgageAmort])

  const termYears = Math.floor(termMonths / 12)
  const termRemainder = termMonths % 12

  // PDF Export
  const handleExportPDF = async () => {
    trackExportReport()
    const { default: jsPDF } = await import('jspdf')
    const { default: autoTable } = await import('jspdf-autotable')

    const doc = new jsPDF()
    const m = 14
    let y = m

    const heading = (text, size = 16) => {
      doc.setFontSize(size)
      doc.setTextColor(0)
      doc.text(text, m, y)
      y += size === 16 ? 8 : 6
    }
    const subtext = (text) => {
      doc.setFontSize(9)
      doc.setTextColor(100)
      const lines = doc.splitTextToSize(text, doc.internal.pageSize.getWidth() - m * 2)
      doc.text(lines, m, y)
      y += lines.length * 4 + 2
    }
    const checkPage = (need = 30) => {
      if (y + need > doc.internal.pageSize.getHeight() - m) {
        doc.addPage()
        y = m
      }
    }

    // ===== Page 1: Title =====
    doc.setFontSize(22)
    doc.setTextColor(0)
    doc.text(`${preset.label} Calculator Report`, m, y + 10)
    y += 14
    doc.setFontSize(10)
    doc.setTextColor(128)
    doc.text(`Generated by Liquid Finance  |  ${new Date().toLocaleDateString()}  |  Loan Type: ${preset.label}`, m, y)
    y += 10

    // ===== Loan Details (inputs) =====
    heading('Loan Details')
    const inputRows = [
      [preset.isMortgage ? 'Home Price' : 'Loan Amount', formatCurrency(principal)],
      ['Interest Rate', `${rate}%`],
      ['Term', `${termYears}y${termRemainder ? ` ${termRemainder}m` : ''} (${termMonths} months)`],
    ]
    if (preset.hasDownPayment) {
      inputRows.push(
        ['Down Payment', `${formatCurrency(effectiveDownPayment)} (${effectiveDownPaymentPercent.toFixed(1)}%)`],
      )
    }
    inputRows.push(['Loan Amount (after down)', formatCurrency(result.loanAmount)])
    if (extraPayment > 0) {
      inputRows.push(['Extra Monthly Payment', formatCurrencyExact(extraPayment)])
    }
    autoTable(doc, {
      body: inputRows,
      startY: y,
      theme: 'grid',
      styles: { fontSize: 9 },
      columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 } },
    })
    y = doc.lastAutoTable.finalY + 8

    // ===== Mortgage Costs (if mortgage) =====
    if (preset.isMortgage && extras) {
      checkPage(40)
      heading('Mortgage Costs')
      const pmiNote = effectiveDownPaymentPercent >= 20 && effectivePmiRate === 0 && pmiRate > 0
        ? ' (PMI waived -- 20%+ down)'
        : ''
      if (pmiNote) subtext('PMI waived (20%+ down payment)')
      autoTable(doc, {
        body: [
          ['Property Tax/mo', formatCurrencyExact(extras.monthlyPropertyTax)],
          ['Property Tax Rate', `${effectivePropertyTaxRate.toFixed(2)}% (${formatCurrency(effectiveAnnualPropertyTax)}/year)`],
          ['Homeowner\'s Insurance/mo', formatCurrencyExact(extras.monthlyInsurance)],
          ['Annual Insurance', formatCurrency(annualInsurance) + '/year'],
          ['PMI Rate', effectivePmiRate > 0 ? `${pmiRate}%/year` : `${pmiRate}%/year (waived)`],
          ['PMI/mo', effectivePmiRate > 0 ? formatCurrencyExact(extras.monthlyPMI) : '$0 (waived)'],
          ...(pmiDropMonth ? [['PMI Drops At', `Month ${pmiDropMonth}`]] : []),
          ['HOA Fees/mo', formatCurrencyExact(extras.monthlyHOA)],
        ],
        startY: y,
        theme: 'grid',
        styles: { fontSize: 9 },
        columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 } },
      })
      y = doc.lastAutoTable.finalY + 8
    }

    // ===== Result Summary Cards =====
    checkPage(50)
    heading('Results')

    if (preset.isMortgage && extras) {
      // Mortgage results: 5 cards + breakdown
      autoTable(doc, {
        head: [['P&I Payment', 'Tax & Insurance', 'PMI', 'HOA', 'Total Monthly']],
        body: [[
          formatCurrencyExact(result.monthlyPayment),
          formatCurrencyExact(extras.monthlyPropertyTax + extras.monthlyInsurance),
          effectivePmiRate > 0 ? formatCurrencyExact(extras.monthlyPMI) : 'Waived',
          formatCurrencyExact(extras.monthlyHOA),
          formatCurrencyExact(totalMonthly),
        ]],
        startY: y,
        theme: 'grid',
        styles: { fontSize: 9, halign: 'center' },
        headStyles: { fillColor: [80, 80, 80] },
      })
      y = doc.lastAutoTable.finalY + 6

      // Monthly breakdown table
      checkPage(40)
      heading('Monthly Payment Breakdown')
      const breakdownRows = [
        ['Principal & Interest', formatCurrencyExact(result.monthlyPayment), `${(result.monthlyPayment / totalMonthly * 100).toFixed(0)}%`],
        ['Property Tax', formatCurrencyExact(extras.monthlyPropertyTax), `${(extras.monthlyPropertyTax / totalMonthly * 100).toFixed(0)}%`],
        ['Homeowner\'s Insurance', formatCurrencyExact(extras.monthlyInsurance), `${(extras.monthlyInsurance / totalMonthly * 100).toFixed(0)}%`],
      ]
      if (effectivePmiRate > 0) {
        breakdownRows.push([
          `PMI${pmiDropMonth ? ` (drops at month ${pmiDropMonth})` : ''}`,
          formatCurrencyExact(extras.monthlyPMI),
          `${(extras.monthlyPMI / totalMonthly * 100).toFixed(0)}%`,
        ])
      }
      if (monthlyHOA > 0) {
        breakdownRows.push(['HOA Fees', formatCurrencyExact(monthlyHOA), `${(monthlyHOA / totalMonthly * 100).toFixed(0)}%`])
      }
      if (extraPayment > 0) {
        breakdownRows.push(['Extra Payment', formatCurrencyExact(extraPayment), `${(extraPayment / totalMonthly * 100).toFixed(0)}%`])
      }
      autoTable(doc, {
        head: [['Component', 'Amount', '% of Total']],
        body: breakdownRows,
        startY: y,
        theme: 'striped',
        styles: { fontSize: 9 },
        headStyles: { fillColor: [80, 80, 80] },
        columnStyles: { 0: { fontStyle: 'bold', cellWidth: 60 } },
      })
      y = doc.lastAutoTable.finalY + 6

      // Additional result cards
      autoTable(doc, {
        head: [['Total Interest', 'Total Cost', 'Payoff Time']],
        body: [[
          formatCurrency(result.totalInterest),
          formatCurrency(result.totalCost),
          `${Math.floor(result.actualTerm / 12)}y ${result.actualTerm % 12}m`,
        ]],
        startY: y,
        theme: 'grid',
        styles: { fontSize: 9, halign: 'center' },
        headStyles: { fillColor: [80, 80, 80] },
      })
      y = doc.lastAutoTable.finalY + 8

    } else {
      // Non-mortgage results: 4 cards
      autoTable(doc, {
        head: [['Monthly Payment', 'Total Interest', 'Total Cost', 'Payoff Time']],
        body: [[
          formatCurrencyExact(result.monthlyPayment),
          formatCurrency(result.totalInterest),
          formatCurrency(result.totalCost),
          `${Math.floor(result.actualTerm / 12)}y ${result.actualTerm % 12}m`,
        ]],
        startY: y,
        theme: 'grid',
        styles: { fontSize: 9, halign: 'center' },
        headStyles: { fillColor: [80, 80, 80] },
      })
      y = doc.lastAutoTable.finalY + 8
    }

    // ===== Extra Payment Savings =====
    if (extraPayment > 0) {
      checkPage(30)
      heading('Extra Payment Savings')
      autoTable(doc, {
        body: [
          ['Extra Payment/mo', formatCurrencyExact(extraPayment)],
          ['Interest Saved', formatCurrency(result.interestSaved)],
          ['Time Saved', `${Math.floor(result.monthsSaved / 12)}y ${result.monthsSaved % 12}m`],
        ],
        startY: y,
        theme: 'grid',
        styles: { fontSize: 10 },
        columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 } },
      })
      y = doc.lastAutoTable.finalY + 8
    }

    // ===== Chart =====
    if (chartData.length > 0) {
      await addChartToPDF(doc, chartRef, { title: 'Loan Balance Over Time' })
    }

    // ===== Amortization Schedule =====
    const schedule = mortgageAmort?.schedule || result.schedule
    if (schedule?.length > 0) {
      doc.addPage()
      y = m
      heading('Amortization Schedule')
      subtext(`${schedule.length} payments total`)

      const amortHead = preset.isMortgage
        ? [['Mo', 'P&I', 'Principal', 'Interest', 'PMI', 'Total', 'Balance']]
        : [['Mo', 'Payment', 'Principal', 'Interest', 'Balance']]

      const amortRows = schedule.map(r =>
        preset.isMortgage
          ? [r.month, formatCurrencyExact(r.payment), formatCurrencyExact(r.principal), formatCurrencyExact(r.interest), r.pmi > 0 ? formatCurrencyExact(r.pmi) : '-', formatCurrencyExact(r.totalWithExtras), formatCurrencyExact(r.balance)]
          : [r.month, formatCurrencyExact(r.payment), formatCurrencyExact(r.principal), formatCurrencyExact(r.interest), formatCurrencyExact(r.balance)]
      )

      autoTable(doc, {
        head: amortHead,
        body: amortRows,
        startY: y,
        theme: 'striped',
        styles: { fontSize: 7 },
        headStyles: { fillColor: [80, 80, 80] },
      })
    }

    doc.save(`${loanType}-calculator-${Date.now()}.pdf`)
  }

  return (
    <div className="space-y-6">
      {/* Loan Type Presets */}
      <GlassCard>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-sm font-semibold text-body mb-3">Loan Type</h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(LOAN_PRESETS).map(([key, p]) => (
                <button
                  key={key}
                  onClick={() => handlePreset(key)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                    loanType === key
                      ? 'text-heading shadow-sm'
                      : 'text-muted hover:text-heading bg-hover'
                  }`}
                  style={loanType === key ? {
                    background: `linear-gradient(135deg, ${colors.primary}22, ${colors.secondary}22)`,
                    border: `1px solid ${colors.primary}44`,
                  } : {}}
                >
                  <span className="text-lg">{p.icon}</span>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          <Button variant="secondary" size="sm" onClick={handleExportPDF} disabled={!isPro} title={!isPro ? 'Pro feature' : ''}>
            Export PDF{!isPro ? ' (Pro)' : ''}
          </Button>
        </div>
      </GlassCard>

      {/* Inputs */}
      <GlassCard>
        <h3 className="text-sm font-semibold text-body mb-4">Loan Details</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <InputField
            label={preset.isMortgage ? 'Home Price' : 'Loan Amount'}
            value={principal}
            onChange={setPrincipal}
            prefix="$"
            type="number"
            min={0}
          />
          <InputField
            label="Interest Rate"
            value={rate}
            onChange={setRate}
            suffix="%"
            type="number"
            step={0.1}
            min={0}
          />
          <InputField
            label={`Term (${termYears}y${termRemainder ? ` ${termRemainder}m` : ''})`}
            value={termMonths}
            onChange={setTermMonths}
            suffix="months"
            type="number"
            min={1}
          />
          {preset.hasDownPayment && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-medium text-muted">
                  Down Payment
                  {preset.isMortgage && (
                    <span className="ml-1 text-faint">
                      ({effectiveDownPaymentPercent.toFixed(1)}%)
                    </span>
                  )}
                </label>
                <div className="flex rounded-lg border border-card overflow-hidden">
                  <button
                    onClick={() => {
                      setDownPaymentMode('dollar')
                      setDownPayment(effectiveDownPayment)
                    }}
                    className={`px-2 py-0.5 text-xs font-medium transition ${
                      downPaymentMode === 'dollar' ? 'bg-active text-heading' : 'text-faint hover:text-muted'
                    }`}
                  >
                    $
                  </button>
                  <button
                    onClick={() => {
                      setDownPaymentMode('percent')
                      setDownPaymentPercent(principal > 0 ? Math.round((downPayment / principal) * 1000) / 10 : 0)
                    }}
                    className={`px-2 py-0.5 text-xs font-medium transition ${
                      downPaymentMode === 'percent' ? 'bg-active text-heading' : 'text-faint hover:text-muted'
                    }`}
                  >
                    %
                  </button>
                </div>
              </div>
              <div className="relative">
                {downPaymentMode === 'dollar' ? (
                  <>
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-faint">$</span>
                    <input
                      type="number"
                      value={downPayment}
                      onChange={(e) => setDownPayment(parseFloat(e.target.value) || 0)}
                      min={0}
                      className="w-full bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:ring-1 pl-7 pr-3"
                    />
                  </>
                ) : (
                  <>
                    <input
                      type="number"
                      value={downPaymentPercent}
                      onChange={(e) => setDownPaymentPercent(parseFloat(e.target.value) || 0)}
                      step={0.5}
                      min={0}
                      max={100}
                      className="w-full bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:ring-1 pl-3 pr-16"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-faint">%</span>
                  </>
                )}
              </div>
              {downPaymentMode === 'percent' && (
                <p className="text-xs text-faint mt-1">{formatCurrency(effectiveDownPayment)}</p>
              )}
            </div>
          )}
          <InputField
            label="Extra Monthly Payment"
            value={extraPayment}
            onChange={setExtraPayment}
            prefix="$"
            type="number"
            min={0}
          />
        </div>
      </GlassCard>

      {/* Mortgage Extras */}
      {preset.isMortgage && (
        <GlassCard>
          <h3 className="text-sm font-semibold text-body mb-4">
            Mortgage Costs
            {effectiveDownPaymentPercent >= 20 && effectivePmiRate === 0 && pmiRate > 0 && (
              <span className="ml-2 text-xs font-normal text-positive">PMI waived (20%+ down)</span>
            )}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-xs font-medium text-muted">
                  Property Tax
                  <span className="ml-1 text-faint">
                    {propertyTaxMode === 'dollar'
                      ? `(${effectivePropertyTaxRate.toFixed(2)}%)`
                      : `(${formatCurrency(effectiveAnnualPropertyTax)}/yr)`
                    }
                  </span>
                </label>
                <div className="flex rounded-lg border border-card overflow-hidden">
                  <button
                    onClick={() => {
                      setPropertyTaxMode('dollar')
                      setAnnualPropertyTax(Math.round(effectiveAnnualPropertyTax))
                    }}
                    className={`px-2 py-0.5 text-xs font-medium transition ${
                      propertyTaxMode === 'dollar' ? 'bg-active text-heading' : 'text-faint hover:text-muted'
                    }`}
                  >$</button>
                  <button
                    onClick={() => {
                      setPropertyTaxMode('percent')
                      setPropertyTaxRate(principal > 0 ? Math.round((annualPropertyTax / principal) * 10000) / 100 : 0)
                    }}
                    className={`px-2 py-0.5 text-xs font-medium transition ${
                      propertyTaxMode === 'percent' ? 'bg-active text-heading' : 'text-faint hover:text-muted'
                    }`}
                  >%</button>
                </div>
              </div>
              <div className="relative">
                {propertyTaxMode === 'dollar' ? (
                  <>
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-faint">$</span>
                    <input
                      type="number"
                      value={annualPropertyTax}
                      onChange={(e) => setAnnualPropertyTax(parseFloat(e.target.value) || 0)}
                      min={0}
                      className="w-full bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:ring-1 pl-7 pr-14"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-faint">/year</span>
                  </>
                ) : (
                  <>
                    <input
                      type="number"
                      value={propertyTaxRate}
                      onChange={(e) => setPropertyTaxRate(parseFloat(e.target.value) || 0)}
                      step={0.1}
                      min={0}
                      className="w-full bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:ring-1 pl-3 pr-16"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-faint">% / year</span>
                  </>
                )}
              </div>
            </div>
            <InputField
              label={`PMI Rate${effectivePmiRate === 0 && pmiRate > 0 ? ' (waived)' : ''}`}
              value={pmiRate}
              onChange={setPmiRate}
              suffix="% / year"
              type="number"
              step={0.05}
              min={0}
              disabled={effectivePmiRate === 0 && effectiveDownPaymentPercent >= 20}
            />
            <InputField
              label="Homeowner's Insurance"
              value={annualInsurance}
              onChange={setAnnualInsurance}
              prefix="$"
              suffix="/year"
              type="number"
              min={0}
            />
            <InputField
              label="HOA Fees"
              value={monthlyHOA}
              onChange={setMonthlyHOA}
              prefix="$"
              suffix="/month"
              type="number"
              min={0}
            />
          </div>
        </GlassCard>
      )}

      {/* Results */}
      {preset.isMortgage && extras ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <ResultCard label="P&I Payment" value={formatCurrencyExact(result.monthlyPayment)} color={colors.primary} />
            <ResultCard label="Tax & Insurance" value={formatCurrencyExact(extras.monthlyPropertyTax + extras.monthlyInsurance)} color={colors.secondary} />
            <ResultCard label="PMI" value={effectivePmiRate > 0 ? formatCurrencyExact(extras.monthlyPMI) : 'Waived'} color={effectivePmiRate > 0 ? colors.warning : colors.positive} />
            <ResultCard label="HOA" value={formatCurrencyExact(extras.monthlyHOA)} color={colors.chartTick} />
            <ResultCard label="Total Monthly" value={formatCurrencyExact(totalMonthly)} color={colors.primary} highlight />
          </div>

          {/* Monthly breakdown */}
          <GlassCard>
            <h3 className="text-sm font-semibold text-body mb-3">Monthly Payment Breakdown</h3>
            <div className="space-y-2">
              <BreakdownRow label="Principal & Interest" amount={result.monthlyPayment} total={totalMonthly} color={colors.primary} />
              <BreakdownRow label="Property Tax" amount={extras.monthlyPropertyTax} total={totalMonthly} color={colors.secondary} />
              <BreakdownRow label="Homeowner's Insurance" amount={extras.monthlyInsurance} total={totalMonthly} color={colors.warning} />
              {effectivePmiRate > 0 && (
                <BreakdownRow label={`PMI${pmiDropMonth ? ` (drops at month ${pmiDropMonth})` : ''}`} amount={extras.monthlyPMI} total={totalMonthly} color={colors.negative} />
              )}
              {monthlyHOA > 0 && (
                <BreakdownRow label="HOA Fees" amount={monthlyHOA} total={totalMonthly} color={colors.chartTick} />
              )}
              {extraPayment > 0 && (
                <BreakdownRow label="Extra Payment" amount={extraPayment} total={totalMonthly} color={colors.positive} />
              )}
            </div>
          </GlassCard>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <ResultCard label="Total Interest" value={formatCurrency(result.totalInterest)} color={colors.negative} />
            <ResultCard label="Total Cost" value={formatCurrency(result.totalCost)} color={colors.secondary} />
            <ResultCard label="Payoff" value={`${Math.floor(result.actualTerm / 12)}y ${result.actualTerm % 12}m`} color={colors.positive} />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <ResultCard label="Monthly Payment" value={formatCurrencyExact(result.monthlyPayment)} color={colors.primary} />
          <ResultCard label="Total Interest" value={formatCurrency(result.totalInterest)} color={colors.negative} />
          <ResultCard label="Total Cost" value={formatCurrency(result.totalCost)} color={colors.secondary} />
          <ResultCard label="Payoff" value={`${Math.floor(result.actualTerm / 12)}y ${result.actualTerm % 12}m`} color={colors.positive} />
        </div>
      )}

      {/* Interest Savings */}
      {extraPayment > 0 && (
        <GlassCard>
          <h3 className="text-sm font-semibold text-body mb-3">Extra Payment Savings</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 rounded-xl bg-subtle">
              <p className="text-xs text-muted mb-1">Interest Saved</p>
              <p className="text-xl font-bold text-positive">{formatCurrency(result.interestSaved)}</p>
            </div>
            <div className="p-3 rounded-xl bg-subtle">
              <p className="text-xs text-muted mb-1">Time Saved</p>
              <p className="text-xl font-bold text-positive">
                {Math.floor(result.monthsSaved / 12)}y {result.monthsSaved % 12}m
              </p>
            </div>
          </div>
        </GlassCard>
      )}

      {/* Chart */}
      {chartData.length > 0 && (
        <GlassCard>
          <h3 className="text-sm font-semibold text-body mb-4">Loan Balance Over Time</h3>
          <div ref={chartRef} className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.chartGrid} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: colors.chartTick }}
                  interval={Math.floor(chartData.length / 6)}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: colors.chartTick }}
                  tickFormatter={v => `$${(v / 1000).toFixed(0)}k`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--card-bg)',
                    border: '1px solid var(--card-border)',
                    borderRadius: '12px',
                    backdropFilter: 'blur(12px)',
                    color: 'var(--heading)',
                  }}
                  formatter={(v) => formatCurrencyExact(v)}
                />
                <Legend />
                <Area type="monotone" dataKey="balance" name="Remaining Balance" stroke={colors.primary} fill={colors.primary} fillOpacity={0.15} strokeWidth={2} />
                <Area type="monotone" dataKey="interestPaid" name="Cumulative Interest" stroke={colors.negative} fill={colors.negative} fillOpacity={0.1} strokeWidth={2} />
                <Area type="monotone" dataKey="principalPaid" name="Cumulative Principal" stroke={colors.positive} fill={colors.positive} fillOpacity={0.1} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>
      )}

      {/* Amortization Table */}
      {(mortgageAmort?.schedule || result.schedule)?.length > 0 && (
        <GlassCard className="!p-0 overflow-hidden">
          <button
            onClick={() => setShowAmortization(!showAmortization)}
            className="w-full flex items-center justify-between px-6 py-4 text-sm font-semibold text-body hover:bg-subtle transition"
          >
            <span>Amortization Schedule ({(mortgageAmort?.schedule || result.schedule).length} payments)</span>
            <svg className={`w-4 h-4 text-muted transition-transform ${showAmortization ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showAmortization && (
            <div className="overflow-x-auto border-t border-card">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-card">
                    <th className="text-left px-4 py-2.5 text-xs font-semibold text-muted">Month</th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-muted">P&I</th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-muted">Principal</th>
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-muted">Interest</th>
                    {preset.isMortgage && mortgageAmort && (
                      <>
                        <th className="text-right px-4 py-2.5 text-xs font-semibold text-muted">PMI</th>
                        <th className="text-right px-4 py-2.5 text-xs font-semibold text-muted">Total</th>
                      </>
                    )}
                    <th className="text-right px-4 py-2.5 text-xs font-semibold text-muted">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {(mortgageAmort?.schedule || result.schedule).map((row) => (
                    <tr key={row.month} className="border-b border-subtle hover:bg-subtle transition text-sm">
                      <td className="px-4 py-2 text-body">{row.month}</td>
                      <td className="px-4 py-2 text-right text-heading font-medium">{formatCurrencyExact(row.payment)}</td>
                      <td className="px-4 py-2 text-right text-positive">{formatCurrencyExact(row.principal)}</td>
                      <td className="px-4 py-2 text-right text-negative">{formatCurrencyExact(row.interest)}</td>
                      {preset.isMortgage && mortgageAmort && (
                        <>
                          <td className={`px-4 py-2 text-right ${row.pmi > 0 ? 'text-warn' : 'text-faint'}`}>
                            {row.pmi > 0 ? formatCurrencyExact(row.pmi) : '-'}
                          </td>
                          <td className="px-4 py-2 text-right text-heading font-medium">{formatCurrencyExact(row.totalWithExtras)}</td>
                        </>
                      )}
                      <td className="px-4 py-2 text-right text-heading">{formatCurrencyExact(row.balance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </GlassCard>
      )}
    </div>
  )
}

function InputField({ label, value, onChange, prefix, suffix, type = 'text', step, min, disabled }) {
  return (
    <div className={disabled ? 'opacity-50' : ''}>
      <label className="block text-xs font-medium text-muted mb-1.5">{label}</label>
      <div className="relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-faint">{prefix}</span>
        )}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
          step={step}
          min={min}
          disabled={disabled}
          className={`w-full bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:ring-1 disabled:cursor-not-allowed ${
            prefix && suffix ? 'pl-7 pr-16' : prefix ? 'pl-7 pr-3' : suffix ? 'pl-3 pr-16' : 'px-3'
          }`}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-faint">{suffix}</span>
        )}
      </div>
    </div>
  )
}

function ResultCard({ label, value, color, highlight }) {
  return (
    <GlassCard className={`!p-4 ${highlight ? 'ring-1' : ''}`} style={highlight ? { borderColor: color + '44' } : {}}>
      <p className="text-xs text-muted mb-1">{label}</p>
      <p className={`font-bold ${highlight ? 'text-xl' : 'text-lg'}`} style={{ color }}>{value}</p>
    </GlassCard>
  )
}

function BreakdownRow({ label, amount, total, color }) {
  const pct = total > 0 ? (amount / total) * 100 : 0
  return (
    <div className="flex items-center gap-3">
      <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: color }} />
      <span className="text-sm text-body flex-1">{label}</span>
      <span className="text-sm font-medium text-heading w-24 text-right">{formatCurrencyExact(amount)}</span>
      <div className="w-24 h-2 rounded-full bg-subtle overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <span className="text-xs text-muted w-10 text-right">{pct.toFixed(0)}%</span>
    </div>
  )
}
